import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import {
  ActionButton,
  ContactInfoForm,
  ContactInfoStep,
  ContactInfoWorkflow,
  IContactInfo,
} from "halifax";
import { ITravelerStepErrors } from "redmond";
import {
  ContactSelectors,
  GenericChildState,
  getChildState,
  getParentState,
  ParentState,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@capone/checkout";

import "./styles.scss";
import { useIsTravelApp, isVesperTenant, getConfigTenant } from "@capone/common";
import { Event, TEvent } from "../../state/events";
import { ActivityMachineContext } from "../../state/types";
import {
  CONTACT_INFO_NOTICE,
  CONTACT_INFO_NOTICE_NO_COMM,
  CONTACT_INFO_SUBTITLE,
  CONTACT_INFO_TITLE,
  CONTACT_INFO_TITLE_MOBILE,
  CONTINUE_TEXT,
  SAVING_TEXT,
} from "./textConstants";
import { PriceBreakdown } from "../PriceBreakdown";
import { DesktopActivityBookValidationError } from "../DesktopActivityBookWorkflow";
import { ActivityTravelerSelectors } from "../../../../checkout";
import { ActivitySelectors } from "../../state/selectors";
import { getTotalTravelersCount } from "../../../common";
import { AGE_BAND_MISMATCH_NOTICE } from "../textConstants";
import {
  AVAILABLE,
  CHECKOUT_DEFAULT_COMM_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";

export interface ContactInfoProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopActivityBookValidationError[];
  setValidationErrorTypes?: (
    types: DesktopActivityBookValidationError[]
  ) => void;
  contactFormOnly?: boolean;
  isXmodRedesign?: boolean;
}

export const ContactInfo = ({
  isMobile,
  validationErrorTypes,
  setValidationErrorTypes,
  contactFormOnly,
  isXmodRedesign,
}: ContactInfoProps) => {
  const [showErrors, setShowErrors] = useState<ITravelerStepErrors>({
    phone: false,
    email: false,
    travelerSelect: false,
  });

  const [state, send] = useCheckoutState<TEvent, ActivityMachineContext>();

  const parentState = getParentState(state.value) as ParentState;
  const childState = getChildState(state.value);

  const { isTravelApp } = useIsTravelApp();
  const isVesper = isVesperTenant(getConfigTenant());

  const expState = useExperiments();
  const isDefaultCommEnabled =
    getExperimentVariant(expState.experiments, CHECKOUT_DEFAULT_COMM_EXPERIMENT) ===
    AVAILABLE;

  const contactInfo = useSelector(ContactSelectors.getContactInformation);

  const contactInfoLoading = useSelector(
    ContactSelectors.getContactInformationLoading
  );

  const travelersValidated = useSelector(
    ActivityTravelerSelectors.getTravelerStateValidated
  );
  const selectedTravelerCount = useSelector(
    ActivityTravelerSelectors.getSelectedTravelerIds
  ).length;

  const searchedTravelerCount = getTotalTravelersCount(
    useSelector(ActivitySelectors.getActivityTravelerCounts)
  );

  const contactInfoValidated = useSelector(
    ContactSelectors.getContactInformationValidated
  );

  const areSelectedTravelersValidForAgeBands = useSelector(
    ActivityTravelerSelectors.getAreSelectedTravelersValidForAgeBands
  );

  const selectedTravelerCountNotEqualSearched =
    selectedTravelerCount > 0 &&
    selectedTravelerCount !== searchedTravelerCount;

  const canContinue = isDefaultCommEnabled
    ? !selectedTravelerCountNotEqualSearched &&
      areSelectedTravelersValidForAgeBands &&
      travelersValidated
    : isMobile ||
      (!selectedTravelerCountNotEqualSearched &&
        areSelectedTravelersValidForAgeBands &&
        travelersValidated &&
        contactInfoValidated);

  const handleContinue = () => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
      send(Event.NEXT);
    } else if (isDefaultCommEnabled) {
      setValidationErrorTypes?.(["travelers"]);
    } else if (!travelersValidated && !contactInfoValidated) {
      setValidationErrorTypes?.(["travelers", "contact-info"]);
    } else if (
      !travelersValidated ||
      selectedTravelerCountNotEqualSearched ||
      !areSelectedTravelersValidForAgeBands
    ) {
      setValidationErrorTypes?.(["travelers"]);
    } else if (!contactInfoValidated) {
      setValidationErrorTypes?.(["contact-info"]);
    }
  };

  const handleBack = () => {
    send(Event.PREVIOUS);
  };

  const onContactInfoChange = (newContactInfo: IContactInfo) => {
    send({ type: Event.CHANGE, contactInfo: newContactInfo });
  };

  const goToContactInfo = () => [
    send({ type: Event.GO_TO_CONTACT_INFORMATION }),
  ];

  useEffect(() => {
    if (canContinue) {
      setValidationErrorTypes?.([]);
    }
  }, [canContinue]);

  useEffect(() => {
    if (travelersValidated) {
      setValidationErrorTypes?.(
        validationErrorTypes?.filter((error) => error !== "travelers") || []
      );
    }
  }, [travelersValidated]);

  useEffect(() => {
    if (contactInfoValidated) {
      setValidationErrorTypes?.(
        validationErrorTypes?.filter((error) => error !== "contact-info") || []
      );
    }
  }, [contactInfoValidated]);

  if (!isDefaultCommEnabled && isMobile)
    return (
      <ContactInfoWorkflow
        titles={{
          contactInfoTitle: CONTACT_INFO_TITLE_MOBILE,
          contactInfoSubtitle: CONTACT_INFO_SUBTITLE,
          buttonTitle: CONTINUE_TEXT,
        }}
        progressStep={
          parentState === ParentState.review
            ? ContactInfoStep.Main
            : ContactInfoStep.ContactInfoForm
        }
        setProgressStep={(step) => {
          if (parentState === ParentState.review) {
            switch (step) {
              case ContactInfoStep.ContactInfoForm:
                goToContactInfo();
                break;
              case ContactInfoStep.Main:
              default:
                break;
            }
          }
        }}
        contactInfo={contactInfo}
        onContactInfoChange={onContactInfoChange}
        isMobile
        onContinueClick={handleContinue}
        onGoBack={handleBack}
        className={clsx("activity-contact-info-workflow-root", {
          xmod: isXmodRedesign,
          redesigned: isVesper,
        })}
        mobileHeaderElement={<PriceBreakdown isMobile dropdown />}
        customHeader={
          <Box className="activity-book-mobile-contact-info-header">
            <Typography
              className="mobile-contact-info-header-title"
              variant="h3"
            >
              {CONTACT_INFO_TITLE_MOBILE}
            </Typography>
            <Typography className="mobile-contact-info-header-subtitle">
              {CONTACT_INFO_SUBTITLE}
            </Typography>
            <hr className="heading-rule" />
          </Box>
        }
        showMobilePopoverTransition={false}
        contactFormOnly={contactFormOnly}
        hideBackButton={isTravelApp}
      />
    );

  const contactInfoNotice = isDefaultCommEnabled
    ? CONTACT_INFO_NOTICE_NO_COMM
    : CONTACT_INFO_NOTICE;

  return (
    <>
      {!isDefaultCommEnabled && (
        <ContactInfoForm
          disabled={
            ![
              ParentState.contactInformation,
              ParentState.activityTravelerInformation,
            ].includes(parentState)
          }
          title={CONTACT_INFO_TITLE}
          subtitle={CONTACT_INFO_SUBTITLE}
          contactInfo={contactInfo}
          onContactInfoChange={onContactInfoChange}
          showErrors={showErrors}
          setShowErrors={setShowErrors}
          loading={contactInfoLoading}
          className={clsx("activity-contact-info-form", {
            error: validationErrorTypes?.includes("contact-info"),
          })}
        />
      )}
      <Typography
        className={clsx("activity-contact-info-notice", {
          error: isDefaultCommEnabled
            ? validationErrorTypes?.includes("travelers")
            : validationErrorTypes?.some((error) =>
                ["contact-info", "travelers"].includes(error)
              ),
        })}
      >
        {!selectedTravelerCountNotEqualSearched &&
        !areSelectedTravelersValidForAgeBands
          ? AGE_BAND_MISMATCH_NOTICE
          : contactInfoNotice(searchedTravelerCount)}
      </Typography>
      <ActionButton
        onClick={handleContinue}
        message={
          childState === GenericChildState.saving ? SAVING_TEXT : CONTINUE_TEXT
        }
        className="activity-continue-button"
        disabled={childState === GenericChildState.saving}
      />
    </>
  );
};
